import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { Link } from 'gatsby';
import '../design/design-guidelines-styles.css';
import { ColorSwatch } from '../../components/ColorSwatch';
import { magma } from 'react-magma-dom';
import { LeadParagraph } from '../../components/LeadParagraph';
export const _frontmatter = {
  "pageTitle": "Color Design Guidelines",
  "title": "Color",
  "order": 2
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DocsHeading = makeShortcode("DocsHeading");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<DocsHeading type="design" mdxType="DocsHeading">Color</DocsHeading>
<p>{`Color helps express hierarchy, establish brand presence, give meaning, and indicate element states.`}</p>
<h2>{`Introduction`}</h2>
<p>{`Application of the color palette brings a unified and recognizable consistency to Cengage’s array of digital products and interfaces. We are committed to complying with the `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/WCAG/"
      }}>{`Web Content Accessibility Guidelines AA standard contrast ratios`}</a>{`. `}</p>
<p>{`All text should be legible and meet accessibility standards. The WCAG 2.1 guidelines require a 4.5:1 color contrast between text and background for normal text, and 3:1 for large text. `}<strong parentName="p">{`At Cengage we strive to always hit 4.5:1 to avoid any possible issues or questions about whether a piece of text is accessible.`}</strong>{` Additionally, any non-text visual information provided that is necessary for a user to identify that a control is present and how to operate it must have a minimum 3:1 contrast ratio with the adjacent colors.`}</p>
<h2>{`Primary`}</h2>
<p>{`The primary colors are based on our main brand color. These colors are used in logical ways throughout product and marketing to guide the eye and highlight the important elements.`}</p>
<p>{`We use primary-500 to indicate primary actions, buttons, text links, etc.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.primary700} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-700</p>
        <p>Space Cadet</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>26,30,81</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.primary600} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-600</p>
        <p>Resolution Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>41,47,124</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.primary500} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-500</p>
        <p>Free Speech Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>57,66,176</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.primary400} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-400</p>
        <p>Slate Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary400}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>93,101,203</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.primary300} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-300</p>
        <p>Blue Bell</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary300}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>139,145,218</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.primary200} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-200</p>
        <p>Lavender Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary200}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>186,189,233</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.primary100} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>primary-100</p>
        <p>Quartz</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.primary100}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>232,233,248</p>
      </div>
    </div>
  </ColorSwatch>
</div>
<h2>{`Secondary`}</h2>
<p>{`The secondary colors are based off secondary-500 from the Cengage Higher Ed branding guidelines. It is commonly used for important calls to action on pages where purchases are made.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.secondary700} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>secondary-700</p>
        <p>Golden Poppy</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.secondary700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>236,201,1</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.secondary600} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>secondary-600</p>
        <p>Sizzling Sunrise</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.secondary600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>254,218,13</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.secondary500} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>secondary-500</p>
        <p>Paris Daisy</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.secondary500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>254,228,73</p>
      </div>
    </div>
  </ColorSwatch>
</div>
<h2>{`Tertiary`}</h2>
<p>{`The tertiary colors are based off tertiary-500 from the Cengage Higher Ed branding guidelines. It is commonly used in place of the primary colors on dark backgrounds.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.tertiary700} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>tertiary-700</p>
        <p>Maya Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.tertiary700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>121,165,255</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.tertiary600} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>tertiary-600</p>
        <p>Columbia Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.tertiary600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>162,193,255</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.tertiary500} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>tertiary-500</p>
        <p>Periwinkle Crayola</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.tertiary500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>205,222,255</p>
      </div>
    </div>
  </ColorSwatch>
</div>
<h2>{`Neutrals`}</h2>
<p>{`The neutrals come in varying levels of saturation to create the right level of contrast. Dark neutrals are the primary colors we use for typography. We commonly use light neutrals for type set on a dark background, or for subtle backgrounds that don’t take away from the cleanliness of a page.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.neutral900} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-900</p>
        <p>Black</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral900}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>0,0,0</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral800} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-800</p>
        <p>Jet</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral800}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>45,45,45</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral700} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-700</p>
        <p>Onyx</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>69,69,69</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral600} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-600</p>
        <p>Mortar</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>90,90,90</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral500} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-500</p>
        <p>Sonic Silver</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>112,112,112</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral400} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-400</p>
        <p>Suva Gray</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral400}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>141,141,141</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral300} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-300</p>
        <p>Light Gray</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral300}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>212,212,212</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral200} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-200</p>
        <p>White Smoke</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral200}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>245,245,245</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral100} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>neutral-100</p>
        <p>White</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.neutral100}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>255,255,255</p>
      </div>
    </div>
  </ColorSwatch>
  
</div>
<h2>{`Info`}</h2>
<p>{`The info colors are used to provide users with neutral, informational feedback. They are primarily used within alerts, and to indicate when an interactive element is in the focused state.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.info700} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-700</p>
        <p>Blue Lagoon</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>26,30,81</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.info600} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-600</p>
        <p>Sapphire Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>41,47,124</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.info500} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-500</p>
        <p>French Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>57,66,176</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.info400} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-400</p>
        <p>Deep Sky Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info400}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>93,101,203</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.info300} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-300</p>
        <p>Blue Jeans</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info300}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>139,145,218</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.info200} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-200</p>
        <p>Uranian Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info200}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>186,189,233</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.info100} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>info-100</p>
        <p>Alice Blue</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.info100}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>232,233,248</p>
      </div>
    </div>
  </ColorSwatch>
</div>
<h2>{`Danger`}</h2>
<p>{`The danger colors are mainly used for backgrounds in messages and in error states to draw attention to important information or actions that are destructive or block workflow. You'll find red used in components such as alerts, buttons, and typography.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.danger700} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-700</p>
        <p>Falu Red</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>127,23,20</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger600} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-600</p>
        <p>Carnelian</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>169,31,26</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger500} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-500</p>
        <p>Maximum Red</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>211,40,33</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger400} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-400</p>
        <p>Cinnabar</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger400}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>226,73,67</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger300} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-300</p>
        <p>Light Coral</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger300}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>232,113,109</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger200} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-200</p>
        <p>Sundown</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger200}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>250,174,176</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger100} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>danger-100</p>
        <p>Rose White</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.danger100}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>253,239,238</p>
      </div>
    </div>
  </ColorSwatch>
</div>
<h2>{`Warning`}</h2>
<p>{`The warning colors are used to indicate a warning or that progress is impeded. It is primarily used within the alert components.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.warning700} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-700</p>
        <p>Sepia</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>110,52,14</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.warning600} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-600</p>
        <p>Saddle Brown</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>141,67,17</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.warning500} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-500</p>
        <p>Windsor Tan</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>173,81,21</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.warning400} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-400</p>
        <p>Spanish Orange</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning400}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>224,106,28</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.warning300} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-300</p>
        <p>Persian Orange</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning300}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>233,139,76</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.warning200} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-200</p>
        <p>Apricot</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning200}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>246,205,178</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.warning100} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>warning-100</p>
        <p>Linen</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.warning100}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>252,238,229</p>
      </div>
    </div>
  </ColorSwatch>
</div>
<h2>{`Success`}</h2>
<p>{`We use green to celebrate success. The success colors are commonly used in the alerts and through typography.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.success700} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-700</p>
        <p>Crusoe</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success700}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>15,83,35</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success600} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-600</p>
        <p>Dartmouth Green</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success600}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>19,106,45</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success500} passesDarkTest={false} passesLightTest={true} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-500</p>
        <p>Dark Spring Green</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success500}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>23,128,55</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success400} passesDarkTest={false} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-400</p>
        <p>Lime Green</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success400}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>33,185,78</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success300} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-300</p>
        <p>Emerald</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success300}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>62,221,110</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success200} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-200</p>
        <p>Celadon</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success200}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>172,240,193</p>
      </div>
    </div>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success100} passesDarkTest={true} passesLightTest={false} mdxType="ColorSwatch">
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Name</p>
        <p>success-100</p>
        <p>Honeydew</p>
      </div>
    </div>
    <div className="color-detail-container">
      <div className="color-detail">
        <p className="detail-label">Hex</p>
        <p className="color-hex">{magma.colors.success100}</p>
      </div>
      <div className="color-detail">
        <p className="detail-label">RGB</p>
        <p>227,250,234</p>
      </div>
    </div>
  </ColorSwatch>
</div>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      